import { Braces, RotateCcw, Save } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { stripServerFields, validationMessages } from '@/api/actions'
import { isApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/input'

/**
 * Advanced: the document as JSON. Local checks first (it must parse, and be an object); the server
 * then validates for real and its 422 messages are listed under the box, one per line, so the fix
 * is obvious. A 409 means someone else saved meanwhile — offer the latest instead of overwriting.
 * Server-owned fields (`rev`, `origin`, timestamps) are shown in the header, not in the box.
 */

export interface JsonEditorProps {
  /** The current document (server shape). */
  doc: Record<string, unknown>
  /** Save the edited body (without server fields). Rejects with the server's error. */
  onSave(body: Record<string, unknown>): Promise<unknown>
  /** Throw the edit away and take the server's latest (after a 409). */
  onReload?(): void
  readOnlyNote?: string
}

const pretty = (doc: Record<string, unknown>): string => JSON.stringify(stripServerFields(doc), null, 2)

export function JsonEditor({ doc, onSave, onReload, readOnlyNote }: JsonEditorProps) {
  const id = useId()
  const initial = useMemo(() => pretty(doc), [doc])
  const [base, setBase] = useState(initial)
  const [text, setText] = useState(initial)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)
  // A new server doc replaces the box after our own save, or when nothing was being typed; an
  // edit in progress is kept (a stale save then comes back as a 409 and offers the latest).
  const takeNext = useRef(false)
  const textRef = useRef(text)
  textRef.current = text
  const baseRef = useRef(base)
  baseRef.current = base
  useEffect(() => {
    if (initial === baseRef.current) return
    if (takeNext.current || textRef.current === baseRef.current) setText(initial)
    takeNext.current = false
    setBase(initial)
  }, [initial])

  const local = useMemo((): { body: Record<string, unknown> | null; error: string | null } => {
    try {
      const v: unknown = JSON.parse(text)
      if (!v || typeof v !== 'object' || Array.isArray(v)) return { body: null, error: 'It must be one JSON object: { … }.' }
      return { body: v as Record<string, unknown>, error: null }
    } catch (err) {
      return { body: null, error: `This is not valid JSON: ${err instanceof Error ? err.message : String(err)}` }
    }
  }, [text])

  const dirty = text !== base
  const conflict = isApiError(serverError) && serverError.status === 409
  const messages = serverError && !conflict ? validationMessages(serverError) : []

  const save = async (): Promise<void> => {
    if (!local.body) return
    setSaving(true)
    setServerError(null)
    setSaved(false)
    try {
      takeNext.current = true
      await onSave(local.body)
      setSaved(true)
    } catch (err) {
      takeNext.current = false
      setServerError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-3" data-testid="json-editor">
      <label htmlFor={id} className="inline-flex items-center gap-2 text-sm font-medium text-ink">
        <Braces className="size-4 text-ink-3" aria-hidden />
        The full definition
      </label>
      {readOnlyNote ? <p className="text-xs text-ink-3">{readOnlyNote}</p> : null}
      <Textarea
        id={id}
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setSaved(false)
        }}
        spellCheck={false}
        rows={18}
        aria-invalid={local.error !== null || messages.length > 0}
        aria-describedby={`${id}-status`}
        className="font-mono text-xs leading-5"
      />
      <div id={`${id}-status`} aria-live="polite">
        {local.error ? (
          <p className="text-sm text-danger" role="alert" data-testid="json-local-error">
            {local.error}
          </p>
        ) : null}
        {messages.length > 0 ? (
          <div className="rounded-xl border border-danger/40 bg-danger-soft p-3 text-sm text-danger" role="alert" data-testid="json-server-errors">
            <p className="font-medium">The studio did not accept it:</p>
            <ul className="mt-1 list-disc pl-5">
              {messages.map((m, i) => (
                <li key={`${i}:${m}`}>{m}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {conflict ? (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-warn/40 bg-warn-soft p-3 text-sm text-ink" role="alert">
            <span>It was changed somewhere else while you were editing.</span>
            {onReload ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  takeNext.current = true
                  setServerError(null)
                  onReload()
                }}
              >
                Get the latest
              </Button>
            ) : null}
          </div>
        ) : null}
        {saved && !dirty ? <p className="text-sm text-ok">Saved.</p> : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => void save()} disabled={!dirty || local.error !== null} loading={saving}>
          {saving ? null : <Save aria-hidden />}
          Save
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setText(base)
            setServerError(null)
          }}
          disabled={!dirty}
        >
          <RotateCcw aria-hidden />
          Undo my changes
        </Button>
        {local.body ? (
          <Button variant="ghost" onClick={() => setText(JSON.stringify(local.body, null, 2))} disabled={JSON.stringify(local.body, null, 2) === text}>
            Tidy up
          </Button>
        ) : null}
      </div>
    </div>
  )
}
